import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { withStyles } from 'tss-react/mui';
import { Box, Popper, Grow, Paper, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OptimizationModeToggleButton from './OptimizationModeToggleButton';
import { createActions } from '../../store';
import { trackEvent, AnalyticsConstants } from '../../../../../utilities/analyticsUtilities';

// How many save attempts in a row have to end with a failed step before the card
// recommending Optimization Mode appears above its toggle button.
const SAVE_FAILURE_RECOMMENDATION_THRESHOLD = 3;

const styles = (theme) => ({
    container: {
        position: 'relative',
        display: 'inline-block',
    },
    popper: {
        zIndex: 1301,
        marginBottom: '10px !important',
    },
    card: {
        position: 'relative',
        fontFamily: 'Inter, sans-serif',
        backgroundColor: theme.palette.background.paper,
        color: '#e6edf3',
        padding: theme.spacing(3),
        width: '320px',
        boxShadow: theme.shadows[8],
    },
    closeButton: {
        position: 'absolute',
        top: theme.spacing(1),
        right: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    title: {
        color: 'white',
        fontWeight: 'bold',
        fontFamily: 'Inter, sans-serif',
        marginBottom: theme.spacing(1),
    },
    text: {
        color: theme.palette.grey[400],
        fontSize: '0.875rem',
        lineHeight: 1.4,
        marginBottom: theme.spacing(2),
    },
    turnOnButton: {
        textTransform: 'none',
        fontWeight: 'bold',
        backgroundColor: '#16a34a',
        color: 'white',
        '&:hover': {
            backgroundColor: '#15803d',
        },
    },
});

// Renders the Optimization Mode toggle button together with the recommendation
// card that appears centered above it after repeated failed save attempts.
// The card is only dismissible with its close button, or by turning the mode on.
const OptimizationModeRecommendationCard = ({
    classes, optimizationModeEnabled, consecutiveSaveFailures, recommendationDismissed,
    savingInProgress, setOptimizationMode, setProperty
}) => {
    const anchorRef = useRef(null);

    const open = !optimizationModeEnabled
        && !recommendationDismissed
        && consecutiveSaveFailures >= SAVE_FAILURE_RECOMMENDATION_THRESHOLD;

    const turnOnOptimizationMode = () => {
        setOptimizationMode(true);
        trackEvent('Toggled Optimization Mode', {
            enabled: true,
            category: AnalyticsConstants.VIDEO_CATEGORY
        });
    };

    return (
        <Box className={classes.container}>
            <Box ref={anchorRef}>
                <OptimizationModeToggleButton />
            </Box>
            <Popper
                open={open && Boolean(anchorRef.current)}
                anchorEl={anchorRef.current}
                placement='top'
                transition
                className={classes.popper}
            >
                {({ TransitionProps }) => (
                    <Grow
                        {...TransitionProps}
                        style={{ transformOrigin: 'center bottom' }}
                        timeout={500}
                    >
                        <Paper className={classes.card}>
                            <IconButton
                                className={classes.closeButton}
                                size='small'
                                aria-label='Dismiss'
                                onClick={() => setProperty('optimizationModeRecommendationDismissed', true)}
                            >
                                <CloseIcon fontSize='small' />
                            </IconButton>
                            <Typography variant='h6' className={classes.title}>
                                Editor Problems?
                            </Typography>
                            <Typography variant='body2' className={classes.text}>
                                You can turn on optimization mode to reduce memory usage and improve stability.
                                The editor preview will be limited, but exports will be unchanged by optimization mode.
                            </Typography>
                            <Button
                                fullWidth
                                variant='contained'
                                className={classes.turnOnButton}
                                disabled={savingInProgress}
                                onClick={turnOnOptimizationMode}
                            >
                                Turn on optimization mode
                            </Button>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </Box>
    );
};

const mapStateToProps = (state) => ({
    optimizationModeEnabled: state.create.optimizationModeEnabled,
    consecutiveSaveFailures: state.create.consecutiveSaveFailures,
    recommendationDismissed: state.create.optimizationModeRecommendationDismissed,
    // The toggle itself is blocked mid-save, so the card's button follows suit.
    savingInProgress: state.create.videoSavingState.savingInProgress,
});

const mapDispatchToProps = (dispatch) => ({
    setOptimizationMode: (enabled) => dispatch(createActions.setOptimizationMode(enabled)),
    setProperty: (key, value) => dispatch(createActions.setProperty(key, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(OptimizationModeRecommendationCard, styles));
